import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { UsuarioReadingFiltersDtoProps } from "api/DTOs/UsuarioReadingFiltersDto"
import { deleteDepartamentoById } from "api/Departamento.service"
import { GetUsuarioByFiltersParamsProps } from "api/Usuario.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import UsuariosFilterMenu from "../components/FilterMenuUsuarios"
import UsuariosModalDelete from "../components/ModalDepartamentosDelete"
import UsuariosModal from "../components/ModalUsuarios"
import USUARIOS_COLUMNS from "../components/TableUsuarios/UsuariosColumns"
import "../style.scss"

export interface ModalUsuariosProps {
  isOpen: boolean
  rowData: UsuarioReadingFiltersDtoProps | null
}
interface UsuariosProps {
  items: Array<UsuarioReadingFiltersDtoProps>
  isLoading: boolean
  totalCount: number
  setServiceQuery: Dispatch<SetStateAction<GetUsuarioByFiltersParamsProps>>
  fetchTableData: () => Promise<void>
}

const Usuarios = ({
  items,
  isLoading,
  totalCount,
  setServiceQuery,
  fetchTableData,
}: UsuariosProps) => {
  const { t } = useTranslation()
  const [usuarioModalIsOpen, setUsuarioModalIsOpen] =
    useState<ModalUsuariosProps>({
      isOpen: false,
      rowData: null,
    })
  const [usuarioModalDeleteIsOpen, setUsuarioModalDeleteIsOpen] = useState("")
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    property: undefined,
    sort: null,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 10,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const dtRef = useRef<DataTablePrimeReact<
    UsuarioReadingFiltersDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }
  const closeDepartamentoModal = useCallback(
    () =>
      setUsuarioModalIsOpen({
        rowData: null,
        isOpen: false,
      }),
    []
  )

  return (
    <section className='tw-bg-white tw-h-1/2'>
      <UsuariosFilterMenu
        isLoading={isLoading}
        serviceQuery={setServiceQuery}
        sortingCriteria={sortingCriteria}
        maxResultCount={pagination.itemsPerPage}
        skipCount={pagination.skipCount}
        resetPagination={handleResetPagination}
        totalCount={totalCount}
      />
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-88px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={USUARIOS_COLUMNS(t, setUsuarioModalIsOpen)}
            className='tw-h-full'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            dataKey='id'
            lazy
            scrollable
            size='small'
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
          <PaginationERP
            className={PAGINATION_STYLES}
            totalRecords={totalCount}
            first={pagination.skipCount}
            rows={pagination.itemsPerPage}
            resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
            setPagination={setPagination}
            optionDropdown={[10, 20, 50, 100]}
            texts={{
              textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
              textCurrentPageReport: `${t("pagination.from")}`,
            }}
          />
        </div>
      </div>
      <UsuariosModal
        isOpen={usuarioModalIsOpen?.isOpen}
        close={closeDepartamentoModal}
        refresh={() => {
          closeDepartamentoModal()
          fetchTableData()
        }}
        rowData={usuarioModalIsOpen?.rowData as UsuarioReadingFiltersDtoProps}
      />
      <UsuariosModalDelete
        t={t}
        isOpen={Boolean(usuarioModalDeleteIsOpen)}
        onCancel={() => setUsuarioModalDeleteIsOpen("")}
        onConfirm={async () => {
          await deleteDepartamentoById(usuarioModalDeleteIsOpen).then(() =>
            notification.success()
          )
          setUsuarioModalDeleteIsOpen("")
          fetchTableData()
        }}
      />
    </section>
  )
}

export default Usuarios
